import { APIClient } from '@customerio/hydra-ui-api';
import { get } from 'lodash-es';
import { getAuthHeaders } from '@/services/api/auth-headers';
import { getApiBaseUrl } from '@/services/api/base-url';
import { getApiClientErrorBody, getApiClientErrorStatus } from '@/services/api/errors';
import { accountStore } from '@/services/auth/store';
import { pollDelay, pollTask } from '@/utils/poll-task';
import { isEmptyString } from '@/utils/string';

const CHECKOUT_RETRY_AFTER_FALLBACK_MS = 5000;
const CHECKOUT_RETRY_AFTER_MIN_MS = 1000;
const CHECKOUT_RETRY_AFTER_MAX_MS = 30_000;
const ORDWAY_CHECKOUT_POLL_MAX_ATTEMPTS = 60;
const ORDWAY_CHECKOUT_POLL_INTERVAL_MS = 5000;
const ORDWAY_CHECKOUT_POLL_TIMEOUT_MS = 5 * 60 * 1000;

interface OrdwayCheckoutStatusPaths {
  '/v1/accounts/{account_id}/billing/plans/checkout_status': {
    get: {
      parameters: {
        path: { account_id: string };
        query: { run_id: string; plan_id: string; coupon_code?: string };
      };
      responses: { 200: { content: { 'application/json': unknown } } };
    };
  };
}

// The pinned generated API client predates this route, so this adapter keeps its compatibility type local.
const ordwayCheckoutStatusApi = new APIClient<OrdwayCheckoutStatusPaths>({
  baseUrl: getApiBaseUrl,
  fetchOptions: { credentials: 'include' },
  headers: getAuthHeaders,
});

function readRetryAfter(headers: unknown): string | null | undefined {
  if (headers == null || typeof headers !== 'object') {
    return undefined;
  }

  const readHeader = Reflect.get(headers, 'get');
  if (typeof readHeader !== 'function') {
    return undefined;
  }

  const value = Reflect.apply(readHeader, headers, ['Retry-After']);
  return typeof value === 'string' || value === null ? value : undefined;
}

function getRetryAfter(error: unknown): string | null {
  return readRetryAfter(get(error, 'headers')) ?? readRetryAfter(get(error, 'response.headers')) ?? null;
}

function parseRetryAfterMs(value: string | null, now: number): number {
  if (value == null) {
    return CHECKOUT_RETRY_AFTER_FALLBACK_MS;
  }

  const trimmed = value.trim();
  const seconds = /^\d+$/.test(trimmed) ? Number(trimmed) : undefined;
  const parsedMs = seconds == null ? Date.parse(trimmed) - now : seconds * 1000;
  if (!Number.isFinite(parsedMs) || parsedMs < 0) {
    return CHECKOUT_RETRY_AFTER_FALLBACK_MS;
  }

  return Math.min(CHECKOUT_RETRY_AFTER_MAX_MS, Math.max(CHECKOUT_RETRY_AFTER_MIN_MS, parsedMs));
}

export type PendingOrdwayCheckout = {
  runId: string;
  retryAfterMs: number;
};

/** A recoverable pending checkout described by a structured HTTP 409, if one was supplied. */
export function parsePendingOrdwayCheckout(error: unknown, now = Date.now()): PendingOrdwayCheckout | undefined {
  if (getApiClientErrorStatus(error) !== 409) {
    return undefined;
  }

  const runId = get(getApiClientErrorBody(error), 'errors[0].meta.run_id');
  if (typeof runId !== 'string' || isEmptyString(runId)) {
    return undefined;
  }

  return {
    runId,
    retryAfterMs: parseRetryAfterMs(getRetryAfter(error), now),
  };
}

const ORDWAY_CHECKOUT_STATUSES = ['pending', 'success', 'conflict', 'failed'] as const;

export type OrdwayCheckoutStatus = (typeof ORDWAY_CHECKOUT_STATUSES)[number];

/** The known checkout status in a response body, or undefined for an unknown or malformed body. */
export function parseOrdwayCheckoutStatus(response: unknown): OrdwayCheckoutStatus | undefined {
  const status = get(response, 'status');
  return typeof status === 'string' ? ORDWAY_CHECKOUT_STATUSES.find((known) => known === status) : undefined;
}

export type OrdwayCheckoutStatusInput = {
  accountId: string;
  runId: string;
  planId: string;
  couponCode?: string;
};

export type OrdwayCheckoutPollInput = Omit<OrdwayCheckoutStatusInput, 'accountId'> & {
  initialDelayMs: number;
};

/** Reads one checkout-status response without trusting its body shape. */
export async function readOrdwayCheckoutStatus(
  input: OrdwayCheckoutStatusInput,
  signal: AbortSignal,
): Promise<unknown> {
  const query =
    input.couponCode == null
      ? { run_id: input.runId, plan_id: input.planId }
      : { run_id: input.runId, plan_id: input.planId, coupon_code: input.couponCode };

  return await ordwayCheckoutStatusApi.get('/v1/accounts/{account_id}/billing/plans/checkout_status', {
    params: { path: { account_id: input.accountId }, query },
    signal,
  });
}

/** The nonblank coupon a fresh subscribe request actually sent, when present. */
export function ordwaySubscribeCouponCode(request: unknown): string | undefined {
  const couponCode = get(request, 'coupon_code');
  return typeof couponCode === 'string' && !isEmptyString(couponCode) ? couponCode : undefined;
}

function abortedPollResult(taskSignal: AbortSignal): false {
  if (taskSignal.aborted) {
    return false;
  }

  throw new Error();
}

type PollLifetime = {
  signal: AbortSignal;
  dispose: () => void;
};

function createPollLifetime(taskSignal: AbortSignal): PollLifetime {
  const controller = new AbortController();
  const abort = () => controller.abort();
  const timeoutId = setTimeout(abort, ORDWAY_CHECKOUT_POLL_TIMEOUT_MS);
  taskSignal.addEventListener('abort', abort, { once: true });
  if (taskSignal.aborted) {
    abort();
  }

  return {
    signal: controller.signal,
    dispose: () => {
      clearTimeout(timeoutId);
      taskSignal.removeEventListener('abort', abort);
    },
  };
}

function checkoutStatusInput(accountId: string, input: OrdwayCheckoutPollInput): OrdwayCheckoutStatusInput {
  return {
    accountId,
    runId: input.runId,
    planId: input.planId,
    ...(input.couponCode == null ? {} : { couponCode: input.couponCode }),
  };
}

async function pollOrdwayCheckoutOnce(signal: AbortSignal, input: OrdwayCheckoutStatusInput): Promise<boolean> {
  let response: unknown;
  try {
    response = await readOrdwayCheckoutStatus(input, signal);
  } catch (error) {
    if (signal.aborted) {
      throw error;
    }
    return false;
  }

  if (signal.aborted) {
    throw new Error();
  }

  const status = parseOrdwayCheckoutStatus(response);
  switch (status) {
    case 'success':
      return true;
    case 'pending':
      return false;
    case undefined:
    case 'conflict':
    case 'failed':
      throw new Error();
    default: {
      const exhaustive: never = status;
      throw new Error(String(exhaustive));
    }
  }
}

/** Polls a recovered Ordway checkout until the server explicitly confirms success. */
export const pollForOrdwayCheckoutTask = pollTask(async (signal, input: OrdwayCheckoutPollInput): Promise<boolean> => {
  const account = accountStore.get();
  if (account == null) {
    return false;
  }

  const lifetime = createPollLifetime(signal);

  try {
    if (input.initialDelayMs > 0) {
      await pollDelay(input.initialDelayMs, lifetime.signal);
    }
    if (lifetime.signal.aborted) {
      return abortedPollResult(signal);
    }

    const statusInput = checkoutStatusInput(String(account.id), input);
    for (let attempt = 0; attempt < ORDWAY_CHECKOUT_POLL_MAX_ATTEMPTS; attempt++) {
      if (await pollOrdwayCheckoutOnce(lifetime.signal, statusInput)) {
        return true;
      }

      if (attempt < ORDWAY_CHECKOUT_POLL_MAX_ATTEMPTS - 1) {
        await pollDelay(ORDWAY_CHECKOUT_POLL_INTERVAL_MS, lifetime.signal);
      }
    }

    throw new Error();
  } catch (error) {
    if (lifetime.signal.aborted) {
      return abortedPollResult(signal);
    }
    throw error;
  } finally {
    lifetime.dispose();
  }
});
